import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Loader2, Users, Send, Clock, CheckCircle2, XCircle, BedDouble, Car, Baby,
  UserCheck, Accessibility, Hotel, Compass, Download, Copy,
} from "lucide-react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { toast } from "sonner";

const csvEscape = (v) => {
  const s = v == null ? "" : String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const cellValue = (col, row) => (col.value ? col.value(row) : row[col.key] ?? "");

function downloadCsv(filename, rows, columns) {
  const header = columns.map((c) => csvEscape(c.label)).join(",");
  const body = rows.map((r) => columns.map((c) => csvEscape(cellValue(c, r))).join(",")).join("\n");
  const blob = new Blob([`${header}\n${body}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function copyPlainList(title, rows, columns) {
  const lines = rows.map((r, i) => {
    const bits = columns.map((c) => cellValue(c, r)).filter((v) => String(v).trim());
    return `${i + 1}. ${bits.join(" · ")}`;
  });
  const text = `${title} (${rows.length})\n\n${lines.join("\n") || "None"}`;
  navigator.clipboard.writeText(text).then(
    () => toast.success("Copied list"),
    () => toast.error("Could not copy"),
  );
}

const guestContactCols = [
  { key: "primary_guest_name", label: "Guest" },
  { key: "family_name", label: "Family" },
  { key: "mobile", label: "Mobile" },
  { key: "email", label: "Email" },
];
const invitedParty = { key: "party", label: "Invited", value: (r) => `${r.adults_invited || 0}A · ${r.children_invited || 0}C` };
const attendingParty = { key: "party", label: "Attending", value: (r) => `${r.adults_attending || 0}A · ${r.children_attending || 0}C` };
const rsvpStatus = { key: "rsvp_status", label: "RSVP" };
const activitiesCol = { key: "activities", label: "Activities", value: (r) => (r.activities || []).join(", ") };
const notesCol = { key: "activity_notes", label: "Notes" };

const guestListCols = [...guestContactCols, invitedParty, rsvpStatus];
const attendingCols = [...guestContactCols, attendingParty];
const activityCols = [...guestContactCols, attendingParty, activitiesCol, notesCol];
const roomFullCols = [
  { key: "room_number", label: "Room" },
  { key: "category", label: "Category" },
  { key: "occupancy", label: "Slots", value: (r) => `${r.slots_used || 0}/${r.max_occupancy || 0}` },
  { key: "guests", label: "Guests" },
];
const slotCols = [
  { key: "room_number", label: "Room" },
  { key: "primary_guest_name", label: "Guest" },
  { key: "family_name", label: "Family" },
  { key: "slots_used", label: "Slots" },
  { key: "notes", label: "Notes" },
];

export default function Dashboard() {
  const { eventId } = useOutletContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    if (!eventId) return;
    setLoading(true);
    api.get(`/dashboard/${eventId}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [eventId]);

  const details = data?.details || {};

  const cards = useMemo(() => {
    if (!data) return [];
    return [
      { key: "total_guests", label: "Total Guests", value: data.total_guests, Icon: Users, columns: guestListCols },
      { key: "invitations_sent", label: "Invitations Sent", value: data.invitations_sent, Icon: Send, columns: guestListCols },
      { key: "rsvp_pending", label: "RSVP Pending", value: data.rsvp_pending, Icon: Clock, columns: guestListCols },
      { key: "confirmed", label: "Confirmed", value: data.confirmed, Icon: CheckCircle2, columns: attendingCols },
      { key: "declined", label: "Declined", value: data.declined, Icon: XCircle, columns: guestListCols },
      {
        key: "adults_confirmed",
        label: "Adults Confirmed",
        value: `${data.adults_confirmed || 0}/${data.adults_invited || 0}`,
        Icon: UserCheck,
        columns: attendingCols,
      },
      {
        key: "children_confirmed",
        label: "Children Confirmed",
        value: `${data.children_confirmed || 0}/${data.children_invited || 0}`,
        Icon: Baby,
        columns: attendingCols,
      },
      { key: "accommodation_required", label: "Accommodation", value: data.accommodation_required, Icon: BedDouble, columns: attendingCols },
      { key: "accessible_room_required", label: "Accessible rooms", value: data.accessible_room_required || 0, Icon: Accessibility, columns: attendingCols },
      { key: "transportation_required", label: "Transportation", value: data.transportation_required, Icon: Car, columns: attendingCols, description: "Guests who requested transfers from Jaipur airport, railway station, or bus stations." },
      { key: "activity_requests", label: "Activity requests", value: data.activity_requests || 0, Icon: Compass, columns: activityCols },
      { key: "rooms_full", label: "Rooms (full)", value: `${data.rooms_full || 0}/${data.rooms_total || 0}`, Icon: Hotel, columns: roomFullCols },
      { key: "slots_used", label: "Room slots used", value: `${data.slots_used || 0}/${data.slots_total || 0}`, Icon: BedDouble, columns: slotCols },
    ];
  }, [data]);

  if (loading || !data)
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#1A362D]" />
      </div>
    );

  const pieData = [
    { name: "Confirmed", value: data.confirmed, color: "#1A362D" },
    { name: "Declined", value: data.declined, color: "#B91C1C" },
    { name: "Pending", value: data.rsvp_pending, color: "#D4B483" },
  ];

  const barData = [
    { name: "Adults", count: data.adults_confirmed },
    { name: "Children", count: data.children_confirmed },
    { name: "Accommodation", count: data.accommodation_required },
    { name: "Accessible", count: data.accessible_room_required || 0 },
    { name: "Transport", count: data.transportation_required },
    { name: "Activities", count: data.activity_requests || 0 },
  ];
  const activityInterest = data.activity_interest || [];
  const conciergeRows = details.activity_requests || [];

  const openCard = (card, extra = {}) => {
    setOpen({
      key: card.key,
      title: extra.title || card.label,
      description: extra.description || card.description || `Guests and details behind ${card.label.toLowerCase()}.`,
      filename: extra.filename || `${card.key.replace(/_/g, "-")}.csv`,
      columns: extra.columns || card.columns,
      rows: extra.rows != null ? extra.rows : (details[card.key] || []),
    });
  };

  return (
    <div data-testid="dashboard-page">
      <h1 className="mb-1 text-2xl font-bold tracking-tight">Dashboard</h1>
      <p className="mb-6 text-sm text-[#525252]">
        Live overview of your celebration. Click any number to see who is behind it.
      </p>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
        {cards.map((c) => (
          <button
            type="button"
            key={c.key}
            onClick={() => openCard(c)}
            data-testid={`stat-tile-${c.key}`}
            className="rounded-md border border-[#E5E5E5] bg-white p-5 text-left transition-transform hover:-translate-y-1 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#1A362D]/30"
          >
            <c.Icon className="mb-3 h-5 w-5 text-[#1A362D]" />
            <div className="text-3xl font-bold tracking-tight" data-testid={`stat-${c.label.toLowerCase().replace(/ /g, "-")}`}>
              {c.value}
            </div>
            <div className="mt-1 text-xs uppercase tracking-wide text-[#525252]">{c.label}</div>
            <div className="mt-2 text-[11px] text-[#1A362D]">View details</div>
          </button>
        ))}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="rounded-md border border-[#E5E5E5] bg-white p-6">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-[#525252]">RSVP Status</h2>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label>
                {pieData.map((e) => (
                  <Cell key={e.name} fill={e.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-6">
            {pieData.map((e) => (
              <div key={e.name} className="flex items-center gap-2 text-xs">
                <span className="h-3 w-3 rounded-full" style={{ background: e.color }} />
                {e.name}
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-md border border-[#E5E5E5] bg-white p-6">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-[#525252]">Attendance & Needs</h2>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={barData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EEE" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#1A362D" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-6 rounded-md border border-[#E5E5E5] bg-white p-6" data-testid="activity-report">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-[#525252]">Concierge activity report</h2>
            <p className="mt-1 text-sm text-[#737373]">
              Who asked for which tours and activities. Share this with the concierge to follow up and book.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={conciergeRows.length === 0}
              onClick={() => copyPlainList("Activity requests", conciergeRows, activityCols)}
              data-testid="copy-activity-report"
            >
              <Copy className="mr-1.5 h-3.5 w-3.5" /> Copy list
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={conciergeRows.length === 0}
              className="bg-[#1A362D] hover:bg-[#12271f]"
              onClick={() => downloadCsv("concierge-activity-requests.csv", conciergeRows, activityCols)}
              data-testid="export-activity-report"
            >
              <Download className="mr-1.5 h-3.5 w-3.5" /> Export CSV
            </Button>
          </div>
        </div>

        {activityInterest.length > 0 && (
          <ul className="mb-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {activityInterest.map((a) => (
              <li key={a.id}>
                <button
                  type="button"
                  onClick={() =>
                    openCard(
                      { key: "activity_requests", label: a.name, columns: activityCols },
                      {
                        title: a.name,
                        description: "Invitees interested in this activity.",
                        filename: `${(a.name || "activity").toLowerCase().replace(/\s+/g, "-")}-requests.csv`,
                        rows: a.guests || [],
                      },
                    )
                  }
                  data-testid={`activity-interest-${a.id}`}
                  className="flex w-full items-center justify-between rounded-md border border-[#E5E5E5] px-3 py-2 text-left text-sm hover:bg-[#FAFAFA]"
                >
                  <span>{a.name}</span>
                  <span className="font-semibold">{a.count}</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {conciergeRows.length === 0 ? (
          <p className="text-sm text-[#737373]">No sightseeing, tour, or activity interest yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                {activityCols.map((col) => (
                  <TableHead key={col.key}>{col.label}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {conciergeRows.map((row) => (
                <TableRow key={row.id}>
                  {activityCols.map((col) => (
                    <TableCell key={col.key}>{cellValue(col, row)}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={!!open} onOpenChange={(v) => { if (!v) setOpen(null); }}>
        <DialogContent className="max-h-[85vh] max-w-4xl overflow-y-auto" data-testid="dashboard-detail-dialog">
          <DialogHeader>
            <DialogTitle>{open?.title}</DialogTitle>
            <DialogDescription>
              {open?.description} {open ? `${open.rows.length} record${open.rows.length === 1 ? "" : "s"}.` : ""}
            </DialogDescription>
          </DialogHeader>
          {open && (
            <>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={open.rows.length === 0}
                  onClick={() => copyPlainList(open.title, open.rows, open.columns)}
                >
                  <Copy className="mr-1.5 h-3.5 w-3.5" /> Copy list
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="bg-[#1A362D] hover:bg-[#12271f]"
                  disabled={open.rows.length === 0}
                  onClick={() => downloadCsv(open.filename, open.rows, open.columns)}
                >
                  <Download className="mr-1.5 h-3.5 w-3.5" /> Export CSV
                </Button>
              </div>
              {open.rows.length === 0 ? (
                <p className="py-8 text-center text-sm text-[#737373]">No matching guests yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      {open.columns.map((col) => (
                        <TableHead key={col.key}>{col.label}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {open.rows.map((row, i) => (
                      <TableRow key={row.id || `${row.room_number || "row"}-${i}`}>
                        {open.columns.map((col) => (
                          <TableCell key={col.key}>{cellValue(col, row)}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
